import { FC, ReactNode, useEffect, useRef } from "react";

import { Box, Grid } from "@mui/material";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { LIVE_EDITOR_ANIMATION } from "@/components/LiveEditorElements/liveEditorAnimation";
import { LiveEditorProgressBar } from "@/components/LiveEditorElements/LiveEditorProgressBar";
import { LIVE_FORM_Z_INDEX } from "@/components/LiveEditorElements/liveFormZIndex";

const PULL_TO_CLOSE_THRESHOLD = 80;

export type LiveEditorLayoutProps = {
  slots: {
    steps: ReactNode;
    preview: ReactNode;
    topBar: ReactNode;
    sideBar?: ReactNode;
    actionFooter?: ReactNode;
  };
  ratio: "6-6" | "5-7";
  stepsKey?: string;
  mobilePreview?: {
    isOpen: boolean;
    onClose?: () => void;
  };
  mobileProgressBar?: {
    show: boolean;
    currentStepIndex: number;
    totalNumberOfSteps: number;
  };
};

export const LiveEditorLayout: FC<LiveEditorLayoutProps> = ({
  slots,
  ratio,
  stepsKey,
  mobilePreview,
  mobileProgressBar,
}) => {
  const { isSmallScreen } = useMediaQuery();
  const previewSectionRef = useRef<HTMLDivElement | null>(null);
  const touchStartY = useRef<number | null>(null);
  const showMobilePreview = isSmallScreen && mobilePreview?.isOpen;

  /* Pull-to-close gesture: close mobile preview when pulling down at top of scroll */
  useEffect(() => {
    const previewElement = previewSectionRef.current;
    if (!previewElement || !showMobilePreview || !mobilePreview?.onClose) {
      return;
    }

    const handleTouchStart = (event: TouchEvent) => {
      if (previewElement.scrollTop <= 0) {
        touchStartY.current = event.touches[0].clientY;
      } else {
        touchStartY.current = null;
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (touchStartY.current === null || previewElement.scrollTop > 0) {
        return;
      }

      const currentY = event.touches[0].clientY;
      const pullDistance = currentY - touchStartY.current;

      if (pullDistance > PULL_TO_CLOSE_THRESHOLD) {
        mobilePreview.onClose?.();
        touchStartY.current = null;
      }
    };

    const handleTouchEnd = () => {
      touchStartY.current = null;
    };

    previewElement.addEventListener("touchstart", handleTouchStart, { passive: true });
    previewElement.addEventListener("touchmove", handleTouchMove, { passive: true });
    previewElement.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      previewElement.removeEventListener("touchstart", handleTouchStart);
      previewElement.removeEventListener("touchmove", handleTouchMove);
      previewElement.removeEventListener("touchend", handleTouchEnd);
    };
  }, [showMobilePreview, mobilePreview]);

  const formMdSize = ratio === "5-7" ? 5 : 6;
  const previewMdSize = ratio === "5-7" ? 7 : 6;

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxHeight: "100vh",
        ["@supports (max-height: 100dvh)"]: { maxHeight: "100dvh" },
      }}
      data-test="live-form-editor"
    >
      {slots.topBar}

      <Grid
        container
        direction="row"
        sx={{
          flex: 1,
          minHeight: 0, // Important to allow proper scrolling
          height: "100%",
          backgroundColor: "white",
        }}
        data-test="live-editor-v2-content"
      >
        {/* Editor Navigation (optional sidebar) */}
        {slots.sideBar}

        {/* Form Section */}
        <Grid
          item
          xs={12}
          md={formMdSize}
          sx={{
            height: "100%",
            overflow: "hidden",
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
          data-test="form-section"
        >
          {/* Step Content */}
          <Box sx={{ flex: 1, overflow: "hidden" }} key={stepsKey}>
            {slots.steps}
          </Box>

          {/* Mobile Progress Bar */}
          {isSmallScreen && mobileProgressBar?.show && (
            <LiveEditorProgressBar
              currentStepIndex={mobileProgressBar.currentStepIndex}
              totalNumberOfSteps={mobileProgressBar.totalNumberOfSteps}
            />
          )}

          {/* Action Footer */}
          {slots.actionFooter}
        </Grid>

        {/* Preview Section */}
        {isSmallScreen ? (
          <Box
            ref={previewSectionRef}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 78, // this is the height of the Back to editing button
              overflowY: "scroll",
              backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
              zIndex: LIVE_FORM_Z_INDEX.PREVIEW,
              transition: LIVE_EDITOR_ANIMATION.transition,
              transform: showMobilePreview ? "translateY(0)" : "translateY(-100%)",
              pointerEvents: showMobilePreview ? "auto" : "none",
            }}
            data-test="preview-section-container"
          >
            {slots.preview}
          </Box>
        ) : (
          <Grid
            item
            xs={12}
            md={previewMdSize}
            ref={previewSectionRef}
            sx={{
              height: "100%",
              overflowY: "auto",
              backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
              display: { xs: "none", md: "block" },
            }}
            data-test="preview-section-container"
          >
            {slots.preview}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
